import { Box, css, Stack, Typography } from '@mui/material'
import { EditionPartnerRole } from '@prisma/client'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import RectArrow from 'assets/icons/nucleo/18px_open-rect-arrow-in.svg'
import { MarkdownDisplay } from 'components/common/MarkdownDisplay/MarkdownDisplay'
import { isNotNullOrUndefined } from 'helpers/array'
import { replaceMarkdownEmptyParagraphs } from 'helpers/string'
import { LiveEventById } from 'services/api/database/EventService'
import { unifiedColors } from 'styles/colors'
import {
  LocalizedText,
  OverflowingBox,
  OverflowingButton
} from './Partner.style'
import {
  BodyContainer,
  OrganizerContainer,
  OrganizerLinks,
  StyledLink
} from './map/Organizer.style'

interface PartnerProps {
  information: NonNullable<LiveEventById>['currentEdition']['editionPartners'][number]
}
const Partner = ({
  information: {
    facebookUrl,
    websiteUrl,
    instagramUrl,
    logoUrl,
    name,
    role,
    localizedContents
  }
}: PartnerProps) => {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('event')

  const description =
    localizedContents.length > 0 && isNotNullOrUndefined(localizedContents[0])
      ? localizedContents[0].description
      : null

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    // check if the content is overflowing
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '0')
    const maxHeight = lineHeight * 2

    setIsOverflowing(el.scrollHeight > maxHeight)
  }, [description])

  return (
    <OrganizerContainer>
      <BodyContainer>
        <Typography variant="h4">
          {role === EditionPartnerRole.ORGANIZER ? t('organizer') : t('timer')}
        </Typography>
        <Stack flexDirection="row" alignItems="center" gap={2.5}>
          {logoUrl && (
            <Box
              width={80}
              height={80}
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              css={css`
                border-radius: 50%;
                background-color: ${unifiedColors.surface.default};
                padding: 8px;
              `}
            >
              <Image
                src={logoUrl}
                alt={name ?? 'partner image'}
                width={0}
                height={0}
                style={{ height: 'auto', width: 'auto' }}
                css={css`
                  & path {
                    fill: ${unifiedColors.label.subdued};
                  }
                `}
                sizes="80px"
              />
            </Box>
          )}
          <OrganizerLinks>
            {name && (
              <Typography variant="body1" fontWeight={600} color="text.primary">
                {name}
              </Typography>
            )}
            {websiteUrl && (
              <StyledLink href={websiteUrl} target="_blank" rel="nofollow">
                <span>{t('goToWebsite')}</span>
                <RectArrow />
              </StyledLink>
            )}
            {instagramUrl && (
              <StyledLink href={instagramUrl} target="_blank" rel="nofollow">
                <span>{t('goToInstagram')}</span>
                <RectArrow />
              </StyledLink>
            )}
            {facebookUrl && (
              <StyledLink href={facebookUrl} target="_blank" rel="nofollow">
                <span>{t('goToFacebook')}</span>
                <RectArrow />
              </StyledLink>
            )}
          </OrganizerLinks>
        </Stack>
        {description && (
          <Box sx={{ position: 'relative' }}>
            <LocalizedText
              ref={contentRef}
              variant="body1"
              color="text.primary"
              expanded={expanded}
            >
              <MarkdownDisplay>
                {replaceMarkdownEmptyParagraphs(description)}
              </MarkdownDisplay>
            </LocalizedText>
            {isOverflowing && (
              <OverflowingBox expanded={expanded}>
                <OverflowingButton
                  variant="text"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? t('seeLess') : t('seeMore')}
                </OverflowingButton>
              </OverflowingBox>
            )}
          </Box>
        )}
      </BodyContainer>
    </OrganizerContainer>
  )
}

export default Partner
